import { FloatingFormItem } from '@/components/_common';
import StatusTag from '@/components/_common/StatusTag';
import { useInvoiceSpedList } from '@/core/domain/invoice/invoice/invoice.hooks';
import {
  InvoiceRecipientType,
  InvoiceSpedStatusRegisteredConstants,
  InvoiceType,
} from '@/core/domain/invoice/invoices/invoices.enums';
import {
  useSpedDocumentInvoices,
  useSpedDocumentManageImports,
} from '@/core/domain/sped/sped-documents/sped-documents.hooks';
import {
  SpedDocumentInvoiceRecipientType,
  SpedDocumentInvoiceRecipientTypeLabel,
  SpedDocumentInvoiceType,
  SpedDocumentInvoiceTypeColor,
  SpedDocumentStatus,
} from '@/core/domain/sped/sped-documents/sped-documents.types';
import { useDisclosure } from '@/hooks';
import { useSpedDocumentContext } from '@/pages/FiscalPages/SpedPage/SpedDocumentsPage/SpedDocumentDetailPage';
import Authorities from '@/types/Authority';
import { hasPermissions } from '@/utils/functions/hasPermissions';
import {
  Button,
  Col,
  Form,
  Input,
  Modal,
  Row,
  Select,
  Table,
  Typography,
} from 'antd';
import moment from 'moment';
import { useEffect, useState } from 'react';
import { useHistory } from 'react-router-dom';

interface FormValues {
  number?: string;
  invoiceTypes?: SpedDocumentInvoiceType[];
  recipientType?: SpedDocumentInvoiceRecipientType;
}

export default function SpedDocumentDetailImportInvoice() {
  const spedDocument = useSpedDocumentContext();

  const hasImportPermission =
    hasPermissions([Authorities.SPED_DOCUMENT_IMPORT_PURCHASE]) &&
    !!spedDocument?.status &&
    [
      SpedDocumentStatus.AWAITING_SELECTION,
      SpedDocumentStatus.REOPENED,
      SpedDocumentStatus.REJECTED,
    ].includes(spedDocument?.status);

  const [form] = Form.useForm<FormValues>();

  const formValues = Form.useWatch([], form);

  const periodLabel = moment([
    Number(spedDocument?.assessmentYear),
    Number(spedDocument?.assessmentMonth) - 1,
    1,
  ]).format('MMMM/YYYY');

  const [opened, action] = useDisclosure();

  const history = useHistory();

  const [idsSelected, setIdsSelected] = useState<number[]>([]);

  const spedDocumentManageImports = useSpedDocumentManageImports();

  const { data: invoices, isLoading } = useInvoiceSpedList(
    {
      ...formValues,
      companyId: spedDocument?.companyId as number,
      month: spedDocument?.assessmentMonth as string,
      year: spedDocument?.assessmentYear as string,
    },
    opened,
  );

  const { data: invoicesImported } = useSpedDocumentInvoices({
    spedDocumentId: spedDocument?.id as number,
  });

  useEffect(() => {
    if (invoices?.length) {
      setIdsSelected(
        invoices
          .filter(
            (invoice) =>
              !invoicesImported?.some(
                (imported) => imported.invoiceId === invoice.id,
              ),
          )
          .map((invoice) => invoice.id),
      );
    }
  }, [invoices, invoicesImported]);

  const handleClose = () => {
    setIdsSelected([]);
    form.resetFields();
    action.close();
  };

  const handleSubmit = async () => {
    if (!spedDocument) return;

    await spedDocumentManageImports.mutateAsync({
      importType: 'INVOICE',
      spedDocumentId: spedDocument?.id,
      items: idsSelected.map((id) => ({
        entityId: id,
        entityEnum: 'INVOICE',
      })),
    });

    handleClose();
    history.push('?tab=invoices');
  };

  return (
    <>
      <Button
        onClick={action.open}
        block
        type="primary"
        disabled={!hasImportPermission}
        id="sped-import-invoice"
      >
        Importar NFs
      </Button>

      <Modal
        width={900}
        open={opened}
        onCancel={handleClose}
        title="Importação de notas fiscais"
        okText="Importar"
        okButtonProps={{ disabled: idsSelected.length < 1 }}
        onOk={handleSubmit}
        confirmLoading={spedDocumentManageImports.isLoading}
      >
        <Typography.Title level={4}>
          Notas fiscais emitidas do período ({periodLabel})
        </Typography.Title>

        <Form form={form}>
          <Row style={{ marginBottom: 24, marginTop: 24 }} gutter={[16, 16]}>
            <Col span={24} sm={4} md={6}>
              <FloatingFormItem name="number" label="N° da nota fiscal">
                <Input />
              </FloatingFormItem>
            </Col>

            <Col span={24} sm={4} md={6}>
              <FloatingFormItem
                name="recipientType"
                label="Tipo de destinatário"
              >
                <Select
                  allowClear
                  style={{ width: '100%' }}
                  options={Object.values(InvoiceRecipientType).map((value) => ({
                    value,
                    label: SpedDocumentInvoiceRecipientTypeLabel[value],
                  }))}
                />
              </FloatingFormItem>
            </Col>

            <Col span={24} sm={4} md={6}>
              <FloatingFormItem name="invoiceTypes" label="Tipo de nota fiscal">
                <Select
                  allowClear
                  style={{ width: '100%' }}
                  mode="tags"
                  options={Object.values(InvoiceType)
                    .filter((value) => value !== 'CFE')
                    .map((value) => ({
                      value,
                      label: value,
                    }))}
                />
              </FloatingFormItem>
            </Col>
          </Row>
        </Form>

        <Table
          loading={isLoading}
          dataSource={invoices}
          pagination={false}
          rowKey={(item) => item.id}
          rowSelection={{
            selectedRowKeys: idsSelected,
            onChange: (keys) => setIdsSelected(keys as number[]),
            getCheckboxProps: (item) => ({
              disabled:
                spedDocumentManageImports.isLoading ||
                !!invoicesImported?.some(
                  (imported) => imported.invoiceId === item.id,
                ),
            }),
          }}
          scroll={{ x: true }}
          columns={[
            { title: 'N° da nota fiscal', dataIndex: 'number', width: 200 },
            { title: 'Chave da nota fiscal', dataIndex: 'key', width: 200 },
            {
              title: 'Destinatário',
              width: 300,
              ellipsis: true,
              render: (_, item) => (
                <div
                  style={{ display: 'flex', flexDirection: 'column', gap: 4 }}
                >
                  <span>{item.recipientName}</span>
                  <span style={{ fontSize: 14 }}>
                    {SpedDocumentInvoiceRecipientTypeLabel[item.recipientType]}
                  </span>
                </div>
              ),
            },
            {
              title: 'Status',
              render: (_, item) => {
                const info =
                  InvoiceSpedStatusRegisteredConstants[
                    item.statusRegisteredInPlugStorage
                  ];

                return <StatusTag color={info.color} label={info.label} />;
              },
            },
            {
              title: 'Tipo',
              width: 200,
              render: (_, item) => (
                <StatusTag
                  color={SpedDocumentInvoiceTypeColor[item.invoiceType]}
                  label={item.invoiceType}
                />
              ),
            },
          ]}
        />
      </Modal>
    </>
  );
}
